import React, {useEffect} from 'react';
import {
    ApplianceField,
    useFieldHelpers,
} from 'components/customer/Product/Appliance/helpers/useFieldHelpers';
import styled from 'styled-components';
import {GenericFields} from 'components/customer/Product/Specs/GenericFields';
import {FIELDSET_LAYOUT} from 'components/customer/Product/FieldSet';
import {Col, Form} from 'react-bootstrap';
import {MoreInfo} from 'shared/components/MoreInfo/MoreInfo';
import {useProductContext} from 'contexts/ProductContext';
import {Select} from 'components/customer/Product/Appliance/Select';
import {useApplianceForm} from 'components/customer/Product/Appliance/helpers/useApplianceForm';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {Icon} from 'shared/helpers';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {CustomApplianceDetails} from 'components/customer/Product/Appliance/CustomApplianceDetails';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {useAppSelector} from 'store/customer';
import {selectApplianceOpeningHeight} from 'components/customer/Product/store/productSlice';
import {useFormikContext} from 'formik';
import {Appliance as ApplianceInterface} from 'components/customer/Product/Appliance/entity/Appliance';

interface ApplianceProps {
    type: number;
    field: ApplianceField;
}

export const Appliance = ({type, field}: ApplianceProps) => {
    const isSmallDevice = isDeviceSmall();
    const {productDataStore} = useProductContext();
    const {formConfig, applianceHandler, setApplianceHeight} = useFieldHelpers(
        type,
        field
    );
    const {dialog, showApplianceForm} = useApplianceForm(
        field.name,
        type,
        setApplianceHeight
    );
    const applianceHeight = useAppSelector(selectApplianceOpeningHeight);
    const {values, setFieldValue} = useFormikContext<{
        appliance: ApplianceInterface;
    }>();

    useEffect(() => {
        if (values.appliance && applianceHeight) {
            if (values.appliance.custom && values.appliance.height == null) {
                void setFieldValue('appliance', {
                    ...values.appliance,
                    height: applianceHeight,
                });
                setApplianceHeight(applianceHeight);
            }
        }
    }, [applianceHeight, values.appliance]);

    return (
        <>
            {dialog}
            <ApplianceHeader>
                <h2>{field.displayName}</h2>
            </ApplianceHeader>

            {formConfig ? (
                <GenericFields
                    fields={formConfig}
                    fieldsetName="appliance"
                    fieldsetLayout={FIELDSET_LAYOUT.COMPOUND}
                />
            ) : null}

            <FormGroup>
                <Form.Label column xs={4}>
                    {`${field.displayName} Selection`}
                    <MoreInfo
                        scope={
                            productDataStore?.current &&
                            productDataStore.current.cabinet &&
                            productDataStore.current.cabinet.attributes
                                ? productDataStore.current.cabinet.attributes
                                : {}
                        }
                        info={field.options.moreInfo}
                    />
                </Form.Label>
                <Col xs="12" sm="7">
                    <Select
                        type={type}
                        name={field.name}
                        callback={applianceHandler}
                        showApplianceForm={showApplianceForm}
                    />
                </Col>
                <Col xs="12" sm="1">
                    <OverlayTrigger
                        placement={isSmallDevice ? 'top' : 'right'}
                        overlay="Add a new Appliance">
                        <div>
                            <Icon
                                style={{
                                    width: '25px',
                                    cursor: 'pointer',
                                    marginTop: '6px',
                                    marginLeft: isSmallDevice ? '0' : '-10px',
                                }}
                                data-cy="add-new-appliance"
                                iconName="Button-Plus.svg"
                                onClick={showApplianceForm}
                            />
                        </div>
                    </OverlayTrigger>
                </Col>
            </FormGroup>

            <CustomApplianceDetails onEditClick={showApplianceForm} />
        </>
    );
};

const ApplianceHeader = styled.div`
    border-bottom: 1px solid;
    margin-bottom: 15px;

    > h2 {
        width: fit-content;
        padding: 5px 15px;
    }
`;
